import { Send as SendIcon } from "@mui/icons-material";
import {
  Alert,
  Box,
  CircularProgress,
  Divider,
  Grid,
  IconButton,
  InputAdornment,
  TextField,
  Typography,
} from "@mui/material";
import { useLayoutEffect, useRef, useState } from "react";

import { useRecipientEmergencyChat } from "~hooks/useRecipientEmergencyChat";
import { ChatMessageList } from "~ui/molecules/Chat/ChatMessageList";

interface RecipientEmergencyChatProps {
  dayOfEmergencyId: string;
  accessToken: string;
  /** Highlights messages from this user (from recipient access validation). */
  highlightUserId?: string;
}

/**
 * Live reunification emergency chat — same channel as signed-in staff on the Reunification page (shared transcript).
 */
export const RecipientEmergencyChat = ({
  dayOfEmergencyId,
  accessToken,
  highlightUserId,
}: RecipientEmergencyChatProps) => {
  const chatWindowRef = useRef<HTMLDivElement>(null);
  const [message, setMessage] = useState("");
  const [isSending, setIsSending] = useState(false);

  const { addMessage, connectionError, isConnected, messages } = useRecipientEmergencyChat({
    accessToken,
    dayOfEmergencyId,
    enabled: Boolean(dayOfEmergencyId && accessToken),
  });

  useLayoutEffect(() => {
    if (chatWindowRef?.current) {
      const scrollTop = chatWindowRef.current.scrollHeight - chatWindowRef.current.clientHeight;
      if (chatWindowRef.current.scrollTop !== scrollTop) {
        chatWindowRef.current.scrollTop = chatWindowRef.current.scrollHeight - chatWindowRef.current.clientHeight;
      }
    }
  });

  const handleSend = async () => {
    if (!message.trim() || isSending) {
      return;
    }

    setIsSending(true);
    try {
      await addMessage(message);
      setMessage("");
    } catch (error) {
      console.error("Failed to send emergency chat message:", error);
    } finally {
      setIsSending(false);
    }
  };

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    await handleSend();
  };

  return (
    <Box sx={{ display: "flex", flexDirection: "column", height: "100%" }}>
      {connectionError ? (
        <Alert severity="warning" sx={{ borderRadius: 0 }}>
          {connectionError} Chat will work once the live chat service is available.
        </Alert>
      ) : null}
      <Box
        ref={chatWindowRef}
        sx={{
          flex: 1,
          overflowY: "auto",
          p: 2,
        }}
      >
        <Typography color="text.secondary" sx={{ mb: 1 }} variant="caption">
          Same live chat as school staff on the Reunification emergency command center — not the law enforcement-only
          channel.
        </Typography>
        {connectionError ? (
          <Box sx={{ py: 2 }}>
            <Typography color="text.secondary" variant="body2">
              Messages will load here after the chat connection succeeds.
            </Typography>
          </Box>
        ) : (
          <ChatMessageList highlightUserId={highlightUserId} isConnected={isConnected} messages={messages} />
        )}
      </Box>
      <Divider />
      <Box sx={{ p: 2 }}>
        <form onSubmit={handleSubmit}>
          <Grid container spacing={2} alignItems="center">
            <Grid size="grow">
              <TextField
                fullWidth
                multiline
                maxRows={4}
                placeholder="Type a message…"
                value={message}
                onChange={(e) => setMessage(e.target.value)}
                size="small"
                disabled={!isConnected || isSending || Boolean(connectionError)}
                autoComplete="off"
                onKeyDown={(e) => {
                  if (e.key === "Enter" && !e.shiftKey) {
                    e.preventDefault();
                    void handleSend();
                  }
                }}
                slotProps={{
                  htmlInput: {
                    autoCapitalize: "off",
                    autoComplete: "off",
                    autoCorrect: "off",
                    spellCheck: "false",
                  },

                  input: {
                    endAdornment: isSending && (
                      <InputAdornment position="end">
                        <CircularProgress size={20} />
                      </InputAdornment>
                    ),
                    sx: {
                      "& .MuiInputBase-input": {
                        overflow: "hidden",
                        textOverflow: "ellipsis",
                        whiteSpace: "normal",
                      },
                      alignItems: "flex-start",
                      p: 1,
                    },
                  },
                }}
              />
            </Grid>
            <Grid>
              <IconButton
                onClick={() => void handleSend()}
                disabled={!isConnected || isSending || !message.trim() || Boolean(connectionError)}
                color="primary"
                size="medium"
              >
                <SendIcon />
              </IconButton>
            </Grid>
          </Grid>
        </form>
      </Box>
    </Box>
  );
};
